/* recently-viewed.js - one file, two roles, because they are two halves of one
   contract and splitting them would let a rail ship without the recorder that
   fills it.

   ROLE A, THE RECORDER. Every element carrying [data-recently-viewed-record]
   publishes a product id. Its id is deduped out of the stored array, unshifted
   to the front and the array is capped at 20. The store is localStorage under
   `pp:recently-viewed`, a JSON array of integers, newest first. The `pp:`
   namespace is this theme's, alongside `pp:compare-on`; the live theme's
   `hypertheme:recently-viewed` is deliberately NOT reused, so a shopper who has
   both themes in front of them at any point never sees one write the other's
   history.

   NOTHING BUT IDS IS STORED. Snapshotting a title, a price and an image would
   render the rail without a request, and it would render it WRONG: a price
   changes, a product sells out, an image is replaced, and a card drawn from
   three week old localStorage would lie about all three. So the ids are the
   whole record and the markup is fetched.

   ROLE B, THE RAIL. Every element carrying [data-recently-viewed] holds a data
   URL ending in `q=`. The stored ids, minus the product being looked at right
   now, minus everything past the section's own limit, become an
   `id:A OR id:B` query appended to it. The response is a render of the same
   section in the context of /search, and its innerHTML replaces the shell's.

   IT FETCHES ON IDLE, NOT ON INTERSECTION. sections/related-products.liquid arms
   its fetch with an IntersectionObserver, and it can: its shell wears the
   section padding, so it has a box to observe. This shell deliberately has none,
   because a cart with no history must not gain an invisible white band, so on
   the first render it is a zero height div. Observing a box that does not exist
   is the wrong instrument for the wrong reason, and requestIdleCallback with a
   timeout is what the live theme uses here for exactly this shape. The setTimeout
   branch is the floor for Safari, which still ships no idle callback.

   TEARDOWN IS KEYED BY SECTION ID, never by node. The theme editor re-renders a
   section by replacing its innerHTML and fires shopify:section:load with NO
   matching unload, so by the time we hear about it the element we armed is
   already detached and unreachable. The id is the only stable handle, and a
   sweep drops any entry whose node has left the document.

   THE MODULE SWEEPS, it never binds to one host. assets/theme.js dedupes lazy
   modules by src and a second host asking for this file gets an already resolved
   promise, so a body that armed a single hardcoded selector would leave every
   host after the first dead. init() queries the document and guards per root.

   NO HISTORY IS NOT A FAILURE. A shopper on their first visit has an empty
   array, so no request is made at all, and the shell stays exactly what it
   already was: an empty box that renders zero bytes. A fetch that fails, or that
   comes back with nothing in it, hides the shell rather than leaving a spinner
   spinning or an empty state lying. */
(function () {
  'use strict';

  var flag = document.documentElement;
  if (flag.hasAttribute('data-pp-recently-viewed')) return;
  flag.setAttribute('data-pp-recently-viewed', '');

  var KEY = 'pp:recently-viewed';
  var CAP = 20;
  var RAIL = '[data-recently-viewed]';
  var RECORD = '[data-recently-viewed-record]';

  var watchers = Object.create(null);
  var recorded = Object.create(null);

  /* Safari private mode throws on the read as well as on the write, and a
     corrupt value is as likely as a missing one once an array has lived in a
     browser for a year, so every entry point returns a clean array of positive
     integers or nothing at all. */
  function read() {
    var raw = null;
    try {
      raw = window.localStorage.getItem(KEY);
    } catch (error) {
      return [];
    }
    if (!raw) return [];
    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      return [];
    }
    /* Array.isArray, not a duck typed length test. A STRING has a numeric
       length, so `"1201"` under this key passed the old guard and was walked
       one CHARACTER at a time: parseInt gave 1, 2, 0 and 1, the zero and the
       repeat were dropped by the filters just below, and the rail asked the
       search API for `id:1 OR id:2`, two products this shopper never saw. A
       wasted request that renders a wrong rail is worse than an empty one, and
       nothing else in this theme could have caught it: it needs a corrupt value
       in a real store, which is what state/tools/verify-recently-viewed.mjs
       puts there. assets/compare.js carries the same guard on its own key. */
    if (!Array.isArray(parsed)) return [];
    var out = [];
    for (var i = 0; i < parsed.length && out.length < CAP; i++) {
      var id = parseInt(parsed[i], 10);
      if (id > 0 && out.indexOf(id) === -1) out.push(id);
    }
    return out;
  }

  function write(list) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, CAP)));
    } catch (error) {
      /* Full quota or a blocked store. The rail simply has less to show. */
    }
  }

  /* CONSENT (2026-10-02). Remembering what a shopper looked at, to show it
     back on a later page, is the Customer Privacy API's `preferences` purpose
     ("customer preferences such as language, currency, size, and more",
     shopify.dev/docs/api/customer-privacy). Measured on a Finnish visitor who
     declined: this key was written anyway. So nothing is recorded and no rail
     is armed until preferencesProcessingAllowed() says true. Where no consent
     is required (the US) the API answers true by default, so the only change
     there is that recording waits for the API to load. A refusal also removes
     the key, because a list kept after it would still be read back into a rail,
     which is the processing that was declined. No API, no answer, no write.
     `consent` stays null until the API has answered once. */
  var consent = null;

  function allowed() {
    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api) return null;
    try {
      return api.preferencesProcessingAllowed() === true;
    } catch (error) {
      return false;
    }
  }

  function decide(ok) {
    if (ok === null) return;
    var was = consent;
    consent = ok;
    if (ok) {
      if (was !== true) init();
      return;
    }
    try {
      window.localStorage.removeItem(KEY);
    } catch (error) {
      /* A blocked store holds nothing of ours to remove. */
    }
  }

  /* ROLE A ---------------------------------------------------------------- */

  /* Guarded by product id and not by node: the product page is re-rendered by
     the Section Rendering API on every variant change, so the same recorder
     comes back as a brand new node several times per page view and a per node
     marker would rewrite storage on each one. */
  function record(node) {
    var id = parseInt(node.getAttribute('data-product-id'), 10);
    if (!(id > 0) || recorded[id]) return;
    recorded[id] = true;
    var list = read();
    var at = list.indexOf(id);
    if (at !== -1) list.splice(at, 1);
    list.unshift(id);
    write(list);
  }

  /* ROLE B ---------------------------------------------------------------- */

  function release(id) {
    var watcher = watchers[id];
    if (!watcher) return;
    watcher.cancel();
    delete watchers[id];
  }

  function sweep() {
    Object.keys(watchers).forEach(function (id) {
      if (!watchers[id].node.isConnected) release(id);
    });
  }

  /* The current product is dropped rather than moved to the back: a rail on a
     product page that opens with the product it sits under is telling the
     shopper something they can already see. */
  function terms(node) {
    var list = read();
    var current = parseInt(node.getAttribute('data-product-id'), 10);
    if (current > 0) {
      var at = list.indexOf(current);
      if (at !== -1) list.splice(at, 1);
    }
    var limit = parseInt(node.getAttribute('data-limit'), 10);
    if (limit > 0) list = list.slice(0, limit);
    if (!list.length) return '';
    return list
      .map(function (id) {
        return 'id:' + id;
      })
      .join(' OR ');
  }

  /* TWO CUSTOM ELEMENTS ARRIVE IN THE FETCHED MARKUP AND NEITHER CAN DEFINE
     ITSELF. <pp-slider> and <pp-card-gallery> both upgrade on their own once
     their element is defined, and innerHTML upgrades the instances already
     sitting in the page the moment that happens. What innerHTML will NEVER do
     is execute a <script src>, and a <script src> is exactly how both modules
     ship: the slider's from the section shell, the gallery's emitted per card
     by snippets/card-product.liquid. So each URL travels as DATA on the shell
     and the loader in assets/theme.js appends a real <script> to the head,
     which does execute.

     THE LINE ABOVE USED TO SAY "and NO <script src> that innerHTML would ever
     execute". That was true when it was written and stopped being true the day
     the card started emitting one; it is left recorded here because a comment
     that describes an absence is the kind that rots without a single test going
     red.

     THE GALLERY ARM IS NOT SYMMETRY, IT IS THE ONLY PATH IT HAS. A card is
     drawn server side on the collection, search and home routes, never on the
     routes that carry these rails: a product page and a cart hold no card at
     all until this fetch lands one. So card-gallery.js has never been
     requested, pp-card-gallery is an unknown tag, and the galleries that just
     arrived keep their data-pp-card-idle attribute: no arrows, no tab stop,
     dead. Measured before the fix: 4 inert galleries on the PDP, 1 on the cart,
     0 arrows.

     ONE FETCH FOR ALL OF THEM. assets/theme.js dedupes loadModule by src, so
     the two rails and blocks/product-complementary.liquid asking for the same
     file share a single resolved promise. */
  function armSlider(node) {
    var PP = window.PP;
    var src = node.getAttribute('data-slider-js');
    if (!src || !node.querySelector('pp-slider')) return;
    if (!PP || typeof PP.loadModule !== 'function') return;
    PP.loadModule(src).then(null, function () {});
  }

  function armGallery(node) {
    var PP = window.PP;
    var src = node.getAttribute('data-gallery-js');
    if (!src || !node.querySelector('pp-card-gallery')) return;
    if (!PP || typeof PP.loadModule !== 'function') return;
    PP.loadModule(src).then(null, function () {});
  }

  function settle(node, id) {
    node.removeAttribute('data-loading');
    node.setAttribute('data-loaded', '');
    release(id);
  }

  function load(node, id) {
    if (node.hasAttribute('data-loading') || node.hasAttribute('data-loaded')) return;
    var url = node.getAttribute('data-url');
    if (!url) return;
    var query = terms(node);
    if (!query) {
      settle(node, id);
      return;
    }
    node.setAttribute('data-loading', '');

    fetch(url + encodeURIComponent(query))
      .then(function (response) {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then(function (markup) {
        /* A section render returns the whole section, and a page may hold more
           than one rail, so our own data-section-id is matched first. The id is
           platform data, so the selector it builds escapes " and \ and still
           runs behind a try/catch: an id nobody anticipated may cost us the
           exact match and drop us to first match, never the swap. */
        var doc = new DOMParser().parseFromString(markup, 'text/html');
        var fresh = null;
        try {
          fresh = doc.querySelector(RAIL + '[data-section-id="' + id.replace(/["\\]/g, '\\$&') + '"]');
        } catch (error) {
          fresh = null;
        }
        if (!fresh) fresh = doc.querySelector(RAIL);
        settle(node, id);
        /* An empty body is the honest answer for a history whose products have
           all been deleted or unpublished since. Hiding the shell keeps the cart
           exactly as it was rather than showing a heading over nothing. */
        if (!fresh || fresh.innerHTML.trim() === '') {
          node.hidden = true;
          return;
        }
        node.innerHTML = fresh.innerHTML;
        armSlider(node);
        armGallery(node);
      })
      .catch(function () {
        settle(node, id);
        node.hidden = true;
      });
  }

  function arm(node) {
    var id = node.getAttribute('data-section-id');
    if (!id) return;
    /* Same id, new node: the editor just rebuilt this section, so whatever is
       still pending against the old node has to go before a new one is armed. */
    release(id);
    if (node.hasAttribute('data-loaded') || !node.getAttribute('data-url')) return;

    var handle;
    if (typeof window.requestIdleCallback === 'function') {
      handle = window.requestIdleCallback(
        function () {
          release(id);
          load(node, id);
        },
        { timeout: 1500 }
      );
      watchers[id] = {
        node: node,
        cancel: function () {
          window.cancelIdleCallback(handle);
        }
      };
      return;
    }
    handle = window.setTimeout(function () {
      release(id);
      load(node, id);
    }, 200);
    watchers[id] = {
      node: node,
      cancel: function () {
        window.clearTimeout(handle);
      }
    };
  }

  /* Recording runs before arming, in the same pass, so a rail sharing a page
     with a recorder already knows about the product under it and can leave it
     out of its own query. */
  function init(scope) {
    sweep();
    var root = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(root.querySelectorAll(RECORD), record);
    Array.prototype.forEach.call(root.querySelectorAll(RAIL), arm);
  }

  /* The API is usually not loaded yet when this deferred file runs, so it is
     asked for the same way snippets/pp-interactions.liquid asks. A later
     answer from the banner arrives as visitorConsentCollected, which fires on
     a change only, never on subscription. */
  decide(allowed());
  if (consent === null && window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
      if (!error) decide(allowed());
    });
  }

  document.addEventListener('visitorConsentCollected', function (event) {
    var detail = event && event.detail;
    decide(detail && typeof detail.preferencesAllowed === 'boolean' ? detail.preferencesAllowed : allowed());
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (consent === true) init(event.target);
  });

  document.addEventListener('shopify:section:unload', function () {
    sweep();
  });
})();
